// One plain label per section for the editor rail: the section's own words (a
// hero headline, a heading, an image description, a quote, a button label),
// falling back to the type name. Tags are dropped, a few entities decoded,
// *word* highlight markers lose their stars and whitespace collapses, so the
// label reads the way the page does.
import { SECTION_LABEL, type Section, type SectionType } from '../model/pages';

const ENTITIES: Record<string, string> = {
  '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': '\'', '&apos;': '\'',
};

/** Plain text of a field: no tags, no *highlight* stars, single spaces. */
export function plainLabel(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(?:nbsp|amp|lt|gt|quot|apos|#39);/g, (m) => ENTITIES[m] ?? m)
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Body copy is long: the label keeps its first 60 characters. */
const BODY_MAX = 60;
function bodyLabel(value: string | undefined): string {
  const t = plainLabel(value);
  return t.length > BODY_MAX ? t.slice(0, BODY_MAX).trimEnd() : t;
}

type Candidates = (s: Section) => (string | undefined)[];

// Where each type keeps its main words, in order of preference. Types not
// listed use their heading (or headline).
const CANDIDATES: Partial<Record<SectionType, Candidates>> = {
  hero: (s) => [s.headline, s.eyebrow, s.subheadline],
  text: (s) => [s.heading, bodyLabel(s.body)],
  image: (s) => [s.alt],
  testimonial: (s) => [s.quote, s.attribution],
  cta: (s) => [s.label, s.heading],
  faq: (s) => [s.heading, s.faqs?.find((f) => plainLabel(f.q))?.q],
  footer: (s) => [bodyLabel(s.body)],
  form: () => [],
  nav: (s) => [s.heading],
  banner: (s) => [bodyLabel(s.body), s.label],
  split: (s) => [s.heading, bodyLabel(s.body)],
  guarantee: (s) => [s.heading, bodyLabel(s.body)],
  bio: (s) => [s.heading, bodyLabel(s.body)],
  offer: (s) => [s.heading, s.label],
  divider: () => [],
};

/** The rail label for a section: its own words, else the type name. */
export function sectionLabel(section: Section): string {
  const pick = CANDIDATES[section.type] ?? ((s: Section) => [s.heading, s.headline]);
  for (const c of pick(section)) {
    const t = plainLabel(c);
    if (t) return t;
  }
  return SECTION_LABEL[section.type] ?? 'Section';
}

/** The type name inside a sentence ("Edit hero", "Edit CTA button"): the first
 *  letter lowercased, unless the name starts with an acronym. */
export function typeLabelInline(type: SectionType): string {
  const l = SECTION_LABEL[type] ?? 'section';
  return /^[A-Z]{2}/.test(l) ? l : l.charAt(0).toLowerCase() + l.slice(1);
}
